import { ApiError } from '@libraries/typescript/api_client/error';
import { type paths } from '@libraries/typescript/api_client/generated_api_types';
import {
    type ApiBodyType,
    type ApiPathParamsType,
    type ApiQueryParamsType,
    type ApiResponseType,
    type Fetch,
    type FetchResponse,
    type Mode,
} from '@libraries/typescript/api_client/types';
import { generateUuid } from '@libraries/typescript/utilities';

// Generous per-request timeout. It exists to kill a request that is truly
// stuck (e.g. a hung transpacific connection from APAC to our single
// us-east-1 region), not merely-slow ones, so it sits well above normal
// server processing time.
const REQUEST_TIMEOUT_MS = 30_000;

// Total attempts for a retryable (idempotent) request before giving up: one
// initial try plus two retries. Non-idempotent requests are never retried.
const MAX_REQUEST_ATTEMPTS = 3;

// Base delay for exponential backoff between retries (~0.5s, then ~1s, …).
const RETRY_BASE_DELAY_MS = 500;
const GLOBAL_OVERLOAD_HEADER = 'X-Phoebe-Global-Overload';
const RATE_LIMITED_HEADER = 'X-Phoebe-Rate-Limited';
// The server emits at most 180 seconds. Five minutes leaves protocol headroom
// without letting a bad header disable the client for the rest of a session.
const MAX_RETRY_AFTER_DELAY_MS = 5 * 60 * 1_000;

export type ApiClientNetworkErrorContext = {
    readonly error: unknown;
    readonly apiUrl: string;
    readonly method: string;
    readonly pathTemplate: string;
    readonly requestId: string;
    readonly attempt: number;
    readonly maxAttempts: number;
    readonly mode?: Mode;
    readonly reason?: string;
    readonly bestEffort?: boolean;
};

export type ApiClientNetworkErrorReporter = (
    context: ApiClientNetworkErrorContext,
) => void;

export type ApiClientConfig = {
    readonly baseUrl: string;
    readonly fetch: Fetch;
    readonly getMode?: (requestedMode?: Mode) => Mode | undefined;
    /** Fallback mode for generated clients used outside the web singleton. */
    readonly defaultMode?: Mode;
    readonly onNetworkError?: ApiClientNetworkErrorReporter;
    readonly clientVersion?: string;
};

export type ApiClient = ReturnType<typeof createApiClient>;

type ApiRequestOptions<
    Path extends keyof paths,
    Method extends keyof paths[Path],
> = {
    readonly url: Path;
    readonly body?: ApiBodyType<Path, Method>;
    readonly pathParams?: ApiPathParamsType<Path>;
    readonly queryParams?: ApiQueryParamsType<Path, Method>;
    readonly mode?: Mode;
    readonly reason?: string;
    readonly requestId?: string;
    readonly signal?: AbortSignal;
    readonly timeoutMs?: number;
    // Keep the ordinary request timeout but make only one attempt. Use this
    // for read paths where repeating an expensive failed request would amplify
    // backend overload.
    readonly retry?: boolean;
    // Mark a non-GET request as server-side idempotent to opt into the same
    // timeout and backoff-retry resilience as GET/HEAD requests.
    readonly idempotent?: boolean;
    // Mark a request whose failure is expected to be harmless so reporters can
    // suppress error-level reporting without changing request behavior.
    readonly bestEffort?: boolean;
};

type RawApiRequestOptions<
    Path extends keyof paths,
    Method extends keyof paths[Path],
> = Omit<ApiRequestOptions<Path, Method>, 'body'> & {
    readonly body?: ApiBodyType<Path, Method> | FormData;
    readonly headers?: Record<string, string>;
};

type DefaultRawApiMethod<Path extends keyof paths> =
    'get' extends keyof paths[Path] ? 'get' : keyof paths[Path];

type ApiValidationIssue = {
    readonly loc?: unknown;
    readonly msg?: unknown;
    readonly message?: unknown;
};

type ApiClientCooldown = {
    readonly retryAtMs: number;
    readonly status: number;
    readonly message: string;
};

type ApiRateLimitCooldown = {
    readonly retryAtMs: number;
    readonly scope: string;
    readonly message: string;
};

function isRetryableNetworkError(error: unknown): boolean {
    return error instanceof Error && error.name !== 'AbortError';
}

/**
 * True for a request cancelled through an `AbortSignal`.
 *
 * At the `onNetworkError` / `ApiError` boundary this always means a *caller*
 * cancellation — an unmount, a navigation, a superseded request. A stuck
 * connection never surfaces here: `fetchWithTimeout` re-labels its own timeout
 * abort as a timeout error before the error escapes. Reporters can therefore
 * treat this as routine rather than as a network failure.
 */
export function isAbortError(error: unknown): boolean {
    return error instanceof Error && error.name === 'AbortError';
}

export function shouldRetryApiClientNetworkError(
    context: Pick<
        ApiClientNetworkErrorContext,
        'error' | 'attempt' | 'maxAttempts'
    >,
): boolean {
    return (
        context.attempt < context.maxAttempts &&
        isRetryableNetworkError(context.error)
    );
}

async function sleep(delayMs: number, signal?: AbortSignal): Promise<void> {
    // Make the backoff interruptible so a caller cancellation during the delay
    // is observed promptly instead of waiting out the full backoff before the
    // next attempt is skipped.
    if (signal?.aborted) {
        return;
    }
    await new Promise<void>((resolve) => {
        let settled = false;
        const finish = (): void => {
            if (settled) {
                return;
            }
            settled = true;
            resolve();
        };
        const timeoutId = setTimeout(finish, delayMs);
        if (!signal) {
            return;
        }
        signal.addEventListener(
            'abort',
            () => {
                clearTimeout(timeoutId);
                finish();
            },
            { once: true },
        );
    });
}

function isRetryableMethod(method: string): boolean {
    // Only retry idempotent methods so a retry can never double-submit a
    // mutation (POST/PUT/PATCH/DELETE).
    return method === 'GET' || method === 'HEAD';
}

function isRetryableHttpStatus(status: number): boolean {
    // The backend owns rate-limit recovery. Retrying 429 in the browser before
    // Retry-After elapses can extend a provider penalty window.
    return status >= 500;
}

function isJsonResponse(response: FetchResponse): boolean {
    const contentType = response.headers.get('content-type');
    if (!contentType) {
        return false;
    }
    const mediaType = contentType.split(';', 1)[0]?.trim().toLowerCase();
    return (
        mediaType === 'application/json' ||
        mediaType?.endsWith('+json') === true
    );
}

function getRetryAfterDelayMs(response: FetchResponse): number | null {
    const retryAfter = response.headers.get('Retry-After')?.trim();
    if (!retryAfter) {
        return null;
    }

    const seconds = Number(retryAfter);
    if (Number.isFinite(seconds) && seconds >= 0) {
        const delayMs = seconds * 1_000;
        return Number.isFinite(delayMs)
            ? Math.min(delayMs, MAX_RETRY_AFTER_DELAY_MS)
            : null;
    }

    const retryAtMs = Date.parse(retryAfter);
    if (!Number.isFinite(retryAtMs)) {
        return null;
    }
    return Math.min(
        Math.max(0, retryAtMs - Date.now()),
        MAX_RETRY_AFTER_DELAY_MS,
    );
}

function formatValidationIssueLocation(loc: unknown): string | null {
    if (!Array.isArray(loc)) {
        return null;
    }
    const parts = loc
        .filter((part) => part !== 'body')
        .map((part) => String(part));
    return parts.length > 0 ? parts.join('.') : null;
}

function getApiErrorMessage(errorBody: unknown): string | null {
    if (typeof errorBody === 'string' && errorBody) {
        return errorBody;
    }
    if (!errorBody || typeof errorBody !== 'object') {
        return null;
    }

    const body = errorBody as Record<string, unknown>;
    const detail = body['detail'];
    if (typeof detail === 'string') {
        return detail;
    }
    if (
        detail !== null &&
        typeof detail === 'object' &&
        !Array.isArray(detail)
    ) {
        const detailMessage = (detail as Record<string, unknown>)['message'];
        if (typeof detailMessage === 'string') {
            return detailMessage;
        }
    }
    if (Array.isArray(detail)) {
        const firstIssue = (detail as ReadonlyArray<ApiValidationIssue>)[0];
        if (firstIssue === undefined) {
            return null;
        }
        const message = firstIssue?.msg ?? firstIssue?.message;
        if (typeof message === 'string') {
            const location = formatValidationIssueLocation(firstIssue.loc);
            return location ? `${location}: ${message}` : message;
        }
    }
    const message = body['message'];
    if (typeof message === 'string') {
        return message;
    }
    return null;
}

function getApiErrorRequestId(errorBody: unknown): string | undefined {
    if (!errorBody || typeof errorBody !== 'object') {
        return undefined;
    }

    const body = errorBody as Record<string, unknown>;
    const detail = body['detail'];
    if (
        detail !== null &&
        typeof detail === 'object' &&
        !Array.isArray(detail)
    ) {
        const requestId = (detail as Record<string, unknown>)['request_id'];
        if (typeof requestId === 'string') {
            return requestId;
        }
    }
    const requestId = body['request_id'];
    return typeof requestId === 'string' ? requestId : undefined;
}

function getRetryDelayMs(attempt: number): number {
    // Exponential backoff (~0.5s, ~1s, …) with up to 50% jitter so a fleet of
    // clients failing together (e.g. a regional latency spike) doesn't retry
    // in lockstep and stampede the API.
    const backoffMs = RETRY_BASE_DELAY_MS * 2 ** (attempt - 1);
    return backoffMs + Math.random() * backoffMs * 0.5;
}

function createTimeoutError(timeoutMs: number): Error {
    // Give the timeout its own error name (not 'AbortError') so it is
    // classified as a retryable transient failure, distinct from a genuine
    // caller-initiated cancellation, which must never be retried.
    const error = new Error(`Request timed out after ${timeoutMs}ms`);
    error.name = 'TimeoutError';
    return error;
}

function createCancellationApiError(requestId: string): ApiError {
    // Report a caller cancellation that lands during a retry backoff the same
    // way an in-flight caller abort is reported: as a non-retried abort.
    const abortError = new Error('The operation was aborted');
    abortError.name = 'AbortError';
    return new ApiError(abortError.message, undefined, undefined, undefined, {
        originalError: abortError,
        isNetworkError: true,
        isAbortError: true,
        requestId,
    });
}

function createRateLimitSuppressedApiError(
    cooldown: ApiRateLimitCooldown,
    requestId: string,
): ApiError {
    return new ApiError(cooldown.message, 429, undefined, undefined, {
        rateLimitedScope: cooldown.scope,
        rateLimitedRetryAtMs: cooldown.retryAtMs,
        rateLimitSuppressed: true,
        requestId,
    });
}

async function backoffBeforeRetry(
    attempt: number,
    requestId: string,
    signal?: AbortSignal,
): Promise<void> {
    // Wait out the exponential backoff, then bail if the caller cancelled
    // during the delay so a cancellation is surfaced promptly instead of
    // issuing another attempt.
    await sleep(getRetryDelayMs(attempt), signal);
    if (signal?.aborted) {
        throw createCancellationApiError(requestId);
    }
}

async function fetchWithTimeout<T>(
    fetch: Fetch,
    url: string,
    init: Omit<Parameters<Fetch>[1], 'signal'>,
    callerSignal: AbortSignal | undefined,
    timeoutMs: number,
    consumeResponse: (response: FetchResponse) => Promise<T>,
): Promise<T> {
    // Bound every request with a timeout so a stuck connection (e.g. a hung
    // transpacific round trip) fails fast instead of hanging on the platform
    // default. Use a manual AbortController + setTimeout rather than
    // AbortSignal.timeout/any so this works in React Native too.
    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
    }, timeoutMs);

    // Forward an explicit caller cancellation to our internal controller
    // (without AbortSignal.any) so caller-initiated aborts still work.
    const forwardCallerAbort = (): void => {
        controller.abort();
    };
    if (callerSignal) {
        if (callerSignal.aborted) {
            controller.abort();
        } else {
            callerSignal.addEventListener('abort', forwardCallerAbort);
        }
    }

    try {
        const response = await fetch(url, {
            ...init,
            signal: controller.signal,
        });
        return await consumeResponse(response);
    } catch (error: unknown) {
        // A timeout aborts through our controller and surfaces as an
        // AbortError; re-label it as a timeout so it is retried like other
        // transient failures. A real caller cancellation stays an AbortError
        // so it propagates as a cancellation and is never retried.
        if (
            timedOut &&
            !(callerSignal?.aborted ?? false) &&
            isAbortError(error)
        ) {
            throw createTimeoutError(timeoutMs);
        }
        throw error;
    } finally {
        clearTimeout(timeoutId);
        callerSignal?.removeEventListener('abort', forwardCallerAbort);
    }
}

async function readResponseBody(response: FetchResponse): Promise<unknown> {
    if (response.status === 204) {
        return null;
    }
    if (isJsonResponse(response)) {
        return response.json();
    }
    return response.ok ? response.blob() : response.text();
}

type RawApiResult = {
    readonly response: FetchResponse;
    readonly body: unknown;
};

export function createApiClient(config: ApiClientConfig) {
    // Shared by this client instance so one explicit server cooldown prevents
    // independent SWR consumers from immediately replacing a reconnect herd
    // with a Retry-After herd. Mutations are never blocked by this read-path
    // circuit breaker.
    let cooldown: ApiClientCooldown | null = null;

    function getActiveCooldown(): ApiClientCooldown | null {
        if (cooldown !== null && Date.now() >= cooldown.retryAtMs) {
            cooldown = null;
        }
        return cooldown;
    }

    function applyCooldown(next: ApiClientCooldown): void {
        if (cooldown === null || next.retryAtMs >= cooldown.retryAtMs) {
            cooldown = next;
        }
    }

    let principalRateLimitCooldown: ApiRateLimitCooldown | null = null;
    const routeRateLimitCooldowns = new Map<string, ApiRateLimitCooldown>();

    function buildUrl<
        Path extends keyof paths,
        Method extends keyof paths[Path],
    >(
        options: Pick<
            ApiRequestOptions<Path, Method>,
            'url' | 'pathParams' | 'queryParams'
        >,
    ): string {
        let finalUrl = (options.url as string).slice(1);
        if (options.pathParams) {
            Object.entries(options.pathParams).forEach(([key, value]) => {
                // encodeURIComponent so values with spaces / slashes /
                // unicode (e.g. canonical tag keys like
                // "ok with client smoking") produce well-formed URLs
                // and resolve to the right FastAPI path segment. UUIDs
                // and existing kebab-case slugs encode to themselves,
                // so this is a no-op for those callers.
                finalUrl = finalUrl.replace(
                    `{${key}}`,
                    encodeURIComponent(value),
                );
            });
        }

        let queryString = '';
        if (options.queryParams) {
            const search = new URLSearchParams();
            for (const [key, value] of Object.entries(
                options.queryParams as Record<string, unknown>,
            )) {
                if (value === undefined || value === null) {
                    continue;
                }
                if (Array.isArray(value)) {
                    for (const v of value) {
                        if (v === undefined || v === null || v === '') {
                            continue;
                        }
                        search.append(key, String(v));
                    }
                } else {
                    search.append(key, String(value));
                }
            }
            const serializedQuery = search.toString();
            if (serializedQuery) {
                queryString = `?${serializedQuery}`;
            }
        }

        return `${config.baseUrl}/${finalUrl}${queryString}`;
    }

    async function requestRaw<
        Path extends keyof paths,
        Method extends keyof paths[Path],
    >(
        rawMethod: string,
        options: RawApiRequestOptions<Path, Method>,
        { resilient }: { resilient: boolean },
    ): Promise<RawApiResult> {
        const method = rawMethod.toUpperCase();
        const requestId = options.requestId ?? generateUuid();
        const headers: Record<string, string> = {
            ...options.headers,
            'X-Request-ID': requestId,
        };

        if (config.clientVersion) {
            headers['X-Client-Version'] = config.clientVersion;
        }

        const mode =
            config.getMode?.(options.mode) ??
            options.mode ??
            config.defaultMode;
        if (mode) {
            headers['X-Mode'] = mode;
        }

        if (options.reason) {
            headers['X-Source'] = options.reason;
        }

        let body = options.body;
        if (method !== 'GET') {
            body ??= {} as ApiBodyType<Path, Method>;
        }

        const isFormDataBody =
            typeof FormData !== 'undefined' && body instanceof FormData;
        if (body && !isFormDataBody) {
            headers['Content-Type'] = 'application/json';
        }

        const apiUrl = buildUrl<Path, Method>(options);
        // Several test suites replace global URL with an object-URL stub, so
        // shared request-path code must avoid relying on its constructor.
        const pathname = apiUrl
            .slice(config.baseUrl.length)
            .replace(/\?.*$/, '');
        const routeRateLimitCooldownKey = `${method} ${pathname}`;
        // The client timeout and transient retry only apply to idempotent
        // (GET/HEAD) requests on the JSON request() path. Non-idempotent
        // mutations opt out of the timeout because aborting them client-side
        // would not stop the server from completing, so a slow synchronous
        // POST (e.g. an inline LLM generation) must not be turned into a
        // false failure. Streaming raw() callers (e.g. the agent-run
        // long-poll) own their own reconnect loop and must keep the caller's
        // AbortSignal wired straight to fetch, so they opt out too.
        const applyResilience =
            resilient &&
            (isRetryableMethod(method) || options.idempotent === true);
        const maxAttempts =
            applyResilience && options.retry !== false
                ? MAX_REQUEST_ATTEMPTS
                : 1;
        const timeoutMs = options.timeoutMs ?? REQUEST_TIMEOUT_MS;
        const requestBody: BodyInit | undefined = isFormDataBody
            ? (body as FormData)
            : body
              ? JSON.stringify(body)
              : undefined;
        const requestInit = {
            method,
            headers,
            body: requestBody,
        };

        try {
            for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
                const activeCooldown = isRetryableMethod(method)
                    ? getActiveCooldown()
                    : null;
                if (activeCooldown !== null) {
                    throw new ApiError(
                        activeCooldown.message,
                        activeCooldown.status,
                        undefined,
                        undefined,
                        {
                            globalOverloadRetryAtMs: activeCooldown.retryAtMs,
                            requestId,
                        },
                    );
                }

                // An exact method-and-path cooldown can safely suppress the
                // same mutation. A principal-wide cooldown gates only reads,
                // or one read burst could block an unrelated mutation such as
                // acquiring a websocket ticket and break live features.
                let activePrincipalRateLimitCooldown = isRetryableMethod(method)
                    ? principalRateLimitCooldown
                    : null;
                if (
                    activePrincipalRateLimitCooldown !== null &&
                    Date.now() >= activePrincipalRateLimitCooldown.retryAtMs
                ) {
                    principalRateLimitCooldown = null;
                    activePrincipalRateLimitCooldown = null;
                }
                if (activePrincipalRateLimitCooldown !== null) {
                    throw createRateLimitSuppressedApiError(
                        activePrincipalRateLimitCooldown,
                        requestId,
                    );
                }

                const activeRouteRateLimitCooldown =
                    routeRateLimitCooldowns.get(routeRateLimitCooldownKey);
                if (activeRouteRateLimitCooldown !== undefined) {
                    if (Date.now() >= activeRouteRateLimitCooldown.retryAtMs) {
                        routeRateLimitCooldowns.delete(
                            routeRateLimitCooldownKey,
                        );
                    } else {
                        throw createRateLimitSuppressedApiError(
                            activeRouteRateLimitCooldown,
                            requestId,
                        );
                    }
                }

                let response: FetchResponse;
                let responseBody: unknown = undefined;
                try {
                    if (applyResilience) {
                        const result = await fetchWithTimeout(
                            config.fetch,
                            apiUrl,
                            requestInit,
                            options.signal,
                            timeoutMs,
                            async (candidate) => ({
                                response: candidate,
                                body: await readResponseBody(candidate),
                            }),
                        );
                        response = result.response;
                        responseBody = result.body;
                    } else {
                        response = await config.fetch(apiUrl, {
                            ...requestInit,
                            signal: options.signal,
                        });
                        if (resilient) {
                            responseBody = await readResponseBody(response);
                        }
                    }
                } catch (error: unknown) {
                    const networkErrorContext = {
                        error,
                        apiUrl,
                        method,
                        pathTemplate: options.url as string,
                        requestId,
                        attempt,
                        maxAttempts,
                        mode,
                        reason: options.reason,
                        bestEffort: options.bestEffort,
                    } satisfies ApiClientNetworkErrorContext;
                    const willRetry =
                        shouldRetryApiClientNetworkError(networkErrorContext);
                    config.onNetworkError?.(networkErrorContext);

                    if (!willRetry) {
                        throw new ApiError(
                            error instanceof Error
                                ? error.message
                                : 'Network request failed',
                            undefined,
                            undefined,
                            undefined,
                            {
                                originalError: error,
                                isNetworkError: true,
                                isAbortError: isAbortError(error),
                                requestId,
                            },
                        );
                    }

                    await backoffBeforeRetry(
                        attempt,
                        requestId,
                        options.signal,
                    );
                    continue;
                }

                if (response.ok) {
                    return { response, body: responseBody };
                }

                const retryAfterDelayMs = getRetryAfterDelayMs(response);

                // An explicit Retry-After owns recovery timing. Retrying the
                // same request here would violate it and amplify overload.
                if (
                    attempt < maxAttempts &&
                    retryAfterDelayMs === null &&
                    isRetryableHttpStatus(response.status)
                ) {
                    await backoffBeforeRetry(
                        attempt,
                        requestId,
                        options.signal,
                    );
                    continue;
                }

                let errorMessage = `Request failed with status: ${response.status}`;
                const errorBody =
                    responseBody ?? (await readResponseBody(response));

                errorMessage = getApiErrorMessage(errorBody) ?? errorMessage;
                const retryAtMs =
                    retryAfterDelayMs === null
                        ? undefined
                        : Date.now() + retryAfterDelayMs;
                const isGlobalOverload =
                    retryAtMs !== undefined &&
                    response.headers.get(GLOBAL_OVERLOAD_HEADER) === 'true';
                if (isGlobalOverload) {
                    applyCooldown({
                        retryAtMs,
                        status: response.status,
                        message: errorMessage,
                    });
                }
                const rateLimitedScope =
                    response.status === 429
                        ? response.headers.get(RATE_LIMITED_HEADER)?.trim() ||
                          undefined
                        : undefined;
                const rateLimitedRetryAtMs =
                    rateLimitedScope === undefined ? undefined : retryAtMs;
                if (
                    rateLimitedScope !== undefined &&
                    rateLimitedRetryAtMs !== undefined &&
                    !isGlobalOverload
                ) {
                    const rateLimitCooldown = {
                        retryAtMs: rateLimitedRetryAtMs,
                        scope: rateLimitedScope,
                        message: errorMessage,
                    } satisfies ApiRateLimitCooldown;
                    if (
                        rateLimitedScope === 'user' ||
                        rateLimitedScope === 'org'
                    ) {
                        if (
                            principalRateLimitCooldown === null ||
                            rateLimitCooldown.retryAtMs >=
                                principalRateLimitCooldown.retryAtMs
                        ) {
                            principalRateLimitCooldown = rateLimitCooldown;
                        }
                    } else {
                        const nowMs = Date.now();
                        for (const [
                            cooldownKey,
                            existingCooldown,
                        ] of routeRateLimitCooldowns) {
                            if (nowMs >= existingCooldown.retryAtMs) {
                                routeRateLimitCooldowns.delete(cooldownKey);
                            }
                        }
                        const existingCooldown = routeRateLimitCooldowns.get(
                            routeRateLimitCooldownKey,
                        );
                        if (
                            existingCooldown === undefined ||
                            rateLimitCooldown.retryAtMs >=
                                existingCooldown.retryAtMs
                        ) {
                            routeRateLimitCooldowns.set(
                                routeRateLimitCooldownKey,
                                rateLimitCooldown,
                            );
                        }
                    }
                }

                throw new ApiError(
                    errorMessage,
                    response.status,
                    response,
                    errorBody,
                    {
                        requestId:
                            getApiErrorRequestId(errorBody) ??
                            response.headers.get('x-request-id') ??
                            requestId,
                        globalOverloadRetryAtMs: isGlobalOverload
                            ? retryAtMs
                            : undefined,
                        rateLimitedScope,
                        rateLimitedRetryAtMs,
                        rateLimitSuppressed:
                            response.status === 429 ? false : undefined,
                    },
                );
            }

            throw new Error('Network request failed');
        } catch (error: unknown) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError(
                error instanceof Error
                    ? error.message
                    : 'Network request failed',
                undefined,
                undefined,
                undefined,
                { requestId },
            );
        }
    }

    async function request<
        Path extends keyof paths,
        Method extends keyof paths[Path],
    >(
        method: string,
        options: ApiRequestOptions<Path, Method>,
    ): Promise<ApiResponseType<Path, Method>> {
        const { response, body } = await requestRaw<Path, Method>(
            method,
            options,
            {
                resilient: true,
            },
        );

        if (response.status === 204) {
            return null as ApiResponseType<Path, Method>;
        }

        return body as ApiResponseType<Path, Method>;
    }

    return {
        raw: <
            Path extends keyof paths,
            Method extends keyof paths[Path] = DefaultRawApiMethod<Path>,
        >(
            params: RawApiRequestOptions<Path, Method> & { method: string },
        ) =>
            requestRaw<Path, Method>(params.method, params, {
                resilient: false,
            }).then((result) => result.response),

        get: <
            Path extends keyof paths,
            Method extends keyof paths[Path] & 'get',
        >(params: {
            url: Path;
            pathParams?: ApiPathParamsType<Path>;
            queryParams?: ApiQueryParamsType<Path, Method>;
            mode?: Mode;
            reason?: string;
            requestId?: string;
            signal?: AbortSignal;
            timeoutMs?: number;
            retry?: boolean;
        }) => request<Path, Method>('GET', params),

        post: <
            Path extends keyof paths,
            Method extends keyof paths[Path] & 'post',
        >(params: {
            url: Path;
            body?: ApiBodyType<Path, Method>;
            pathParams?: ApiPathParamsType<Path>;
            queryParams?: ApiQueryParamsType<Path, Method>;
            mode?: Mode;
            reason?: string;
            requestId?: string;
            signal?: AbortSignal;
            idempotent?: boolean;
            bestEffort?: boolean;
        }) => request<Path, Method>('POST', params),

        patch: <
            Path extends keyof paths,
            Method extends keyof paths[Path] & 'patch',
        >(params: {
            url: Path;
            body?: ApiBodyType<Path, Method>;
            pathParams?: ApiPathParamsType<Path>;
            queryParams?: ApiQueryParamsType<Path, Method>;
            mode?: Mode;
            reason?: string;
            requestId?: string;
            signal?: AbortSignal;
            idempotent?: boolean;
        }) => request<Path, Method>('PATCH', params),

        put: <
            Path extends keyof paths,
            Method extends keyof paths[Path] & 'put',
        >(params: {
            url: Path;
            body?: ApiBodyType<Path, Method>;
            pathParams?: ApiPathParamsType<Path>;
            queryParams?: ApiQueryParamsType<Path, Method>;
            mode?: Mode;
            reason?: string;
            requestId?: string;
            signal?: AbortSignal;
        }) => request<Path, Method>('PUT', params),

        delete: <
            Path extends keyof paths,
            Method extends keyof paths[Path] & 'delete',
        >(params: {
            url: Path;
            pathParams?: ApiPathParamsType<Path>;
            queryParams?: ApiQueryParamsType<Path, Method>;
            mode?: Mode;
            reason?: string;
            requestId?: string;
            signal?: AbortSignal;
            idempotent?: boolean;
        }) => request<Path, Method>('DELETE', params),
    };
}
